import {
  Button,
  HStack,
  Icon,
  ICON_SIZES,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  Stack,
  Text,
  useMixpanel,
} from '@talos/kyoko';
import { useCallback } from 'react';
import styled, { useTheme } from 'styled-components';
import { useDisplaySettings } from '../../../providers/DisplaySettingsProvider';

export function SelectNavigationSidebarDisplayMode() {
  const { showSidebarLabels, setShowSidebarLabels } = useDisplaySettings();
  const { type } = useTheme();
  const mixpanel = useMixpanel();

  const handleToggle = useCallback(
    (enabled: boolean) => {
      setShowSidebarLabels(enabled);
      mixpanel.track(MixpanelEvent.ToggleSidebarLabels, {
        [MixpanelEventProperty.Enabled]: enabled,
        [MixpanelEventProperty.Source]: MixpanelEventSource.FeatureShowcase,
      });
    },
    [setShowSidebarLabels, mixpanel]
  );

  return (
    <Stack gap="spacing12">
      Show labels or switch to an icons-only layout for a cleaner, minimal look.
      <HStack gap="spacing24">
        <Stack flex={1} gap="spacing8">
          <ImageToggleButton
            src={`/assets/showcase/sidebar-labels-${type}.png`}
            active={showSidebarLabels}
            onClick={() => handleToggle(true)}
          >
            {showSidebarLabels && <Icon size={ICON_SIZES.LARGE} icon={IconName.CheckCircleSolid} />}
          </ImageToggleButton>
          <Text color="colorTextImportant" fontSize="fontSizeLg">
            Icons + Labels
          </Text>
        </Stack>
        <Stack flex={1} gap="spacing8">
          <ImageToggleButton
            src={`/assets/showcase/sidebar-icons-only-${type}.png`}
            active={!showSidebarLabels}
            onClick={() => handleToggle(false)}
          >
            {!showSidebarLabels && <Icon size={ICON_SIZES.LARGE} icon={IconName.CheckCircleSolid} />}
          </ImageToggleButton>
          <Text color="colorTextImportant" fontSize="fontSizeLg">
            Icons only
          </Text>
        </Stack>
      </HStack>
    </Stack>
  );
}

const ImageToggleButton = styled(Button)<{ src: string; active: boolean }>`
  &,
  &:hover,
  &:focus,
  &:active {
    background-size: cover;
    aspect-ratio: 1.5;
    background-position: center;
    background-repeat: no-repeat;
    width: 100%;
    border-radius: ${({ theme }) => theme.borderRadiusLarge}px;
    background-image: url(${({ src }) => src});
    ${({ active, theme }) =>
      active
        ? `border: 2px solid ${theme.colors.gray['100']}; stack-shadow: 0 0 0 4px ${theme.backgroundModal} inset;`
        : ''};
    position: relative;

    ${Icon} {
      position: absolute;
      bottom: 10px;
      right: 10px;
    }
  }
`;
